import React, { useState, useEffect, useReducer, useContext, useMemo } from "react";

import { makeStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import Paper from "@material-ui/core/Paper";

import TicketListItem from "../TicketListItemCustom";
import TicketsListSkeleton from "../TicketsListSkeleton";

import useTickets from "../../hooks/useTickets";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import useCompanySettings from "../../hooks/useSettings/companySettings";

const useStyles = makeStyles((theme) => {
    const isDark = (theme.palette.type || theme.palette.mode) === 'dark';
    return {
        ticketsListWrapper: {
            position: "relative",
            display: "flex",
            height: "100%",
            flexDirection: "column",
            overflow: "hidden",
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
        },
        ticketsList: {
            flex: 1,
            maxHeight: "100%",
            overflowY: "scroll",
            ...theme.scrollbarStyles,
            height: "60px",
            marginTop: "4px",
        },
        ticketsListHeader: {
            color: isDark ? '#FFF' : theme.palette.text.primary,
            zIndex: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
        },
        ticketsCount: {
            fontWeight: "normal",
            color: isDark ? 'rgba(255,255,255,0.70)' : theme.palette.text.secondary,
            marginLeft: "8px",
            fontSize: "14px",
        },
        noTicketsText: {
            textAlign: "center",
            color: isDark ? 'rgba(255,255,255,0.70)' : theme.palette.text.secondary,
            fontSize: "14px",
            lineHeight: "1.4",
        },
        noTicketsTitle: {
            textAlign: "center",
            fontSize: "16px",
            fontWeight: "600",
            margin: "0px",
            color: isDark ? '#FFF' : theme.palette.text.primary,
        },
        noTicketsDiv: {
            display: "flex",
            margin: 40,
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            opacity: isDark ? 0.95 : 1,
        },
    };
});

const ticketSortAsc = (a, b) => {
    
    if (a.updatedAt < b.updatedAt) {
        return -1;
    }
    if (a.updatedAt > b.updatedAt) {
        return 1;
    }
    return 0;
}

const ticketSortDesc = (a, b) => {
   
    if (a.updatedAt > b.updatedAt) {
        return -1;
    }
    if (a.updatedAt < b.updatedAt) {
        return 1;
    }
    return 0;
}

const reducer = (state, action) => {
    //console.log("action", action, state)
    const sortDir = action.sortDir;
    
    if (action.type === "LOAD_TICKETS") {
        const newTickets = action.payload;

        newTickets.forEach((ticket) => {
            const ticketIndex = state.findIndex((t) => t.id === ticket.id);
            if (ticketIndex !== -1) {
                state[ticketIndex] = ticket;
                if (ticket.unreadMessages > 0) {
                    state.unshift(state.splice(ticketIndex, 1)[0]);
                }
            } else {
                state.push(ticket);
            }
        });
        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? state.sort(ticketSortAsc) : state.sort(ticketSortDesc);
        }

        return [...state];
    }

    if (action.type === "RESET_UNREAD") {
        const ticketId = action.payload;

        const ticketIndex = state.findIndex((t) => t.id === ticketId);
        if (ticketIndex !== -1) {
            state[ticketIndex].unreadMessages = 0;
        }

        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? state.sort(ticketSortAsc) : state.sort(ticketSortDesc);
        }

        return [...state];
    }

    if (action.type === "UPDATE_TICKET") {
        const ticket = action.payload;

        const ticketIndex = state.findIndex((t) => t.id === ticket.id);
        if (ticketIndex !== -1) {
            state[ticketIndex] = ticket;
        } else {
            state.unshift(ticket);
        }
        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? state.sort(ticketSortAsc) : state.sort(ticketSortDesc);
        }

        return [...state];
    }

    if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
        const ticket = action.payload;

        const ticketIndex = state.findIndex((t) => t.id === ticket.id);
        if (ticketIndex !== -1) {
            state[ticketIndex] = ticket;
            state.unshift(state.splice(ticketIndex, 1)[0]);
        } else {
            if (action.status === action.payload.status) {
                state.unshift(ticket);
            }
        }
        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? state.sort(ticketSortAsc) : state.sort(ticketSortDesc);
        }

        return [...state];
    }

    if (action.type === "UPDATE_TICKET_CONTACT") {
        const contact = action.payload;
        const ticketIndex = state.findIndex((t) => t.contactId === contact.id);
        if (ticketIndex !== -1) {
            state[ticketIndex].contact = contact;
        }
        return [...state];
    }

    if (action.type === "DELETE_TICKET") {
        const ticketId = action.payload;
        const ticketIndex = state.findIndex((t) => t.id === ticketId);
        if (ticketIndex !== -1) {
            state.splice(ticketIndex, 1);
        }

        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? state.sort(ticketSortAsc) : state.sort(ticketSortDesc);
        }

        return [...state];
    }

    if (action.type === "RESET") {
        return [];
    }
};

const TicketsListCustom = (props) => {
    const {
        setTabOpen,
        status,
        searchParam,
        searchOnMessages,
        tags,
        users,
        showAll,
        selectedQueueIds,
        updateCount,
        style,
        whatsappIds,
        forceSearch,
        statusFilter,
        userFilter,
        sortTickets,
    } = props;

    const classes = useStyles();
    const [pageNumber, setPageNumber] = useState(1);
    // Flag de criação manual de tickets (empresa)
    const [manualTicketEnabled, setManualTicketEnabled] = useState(false);
    let [ticketsList, dispatch] = useReducer(reducer, []);
    //   const socketManager = useContext(SocketContext);
    const { user, socket } = useContext(AuthContext);
    const { get: getSetting } = useCompanySettings();

    const { profile, queues } = user;
    const showTicketWithoutQueue = user.allTicket === 'enable';
    const companyId = user.companyId;

    // Busca configuração de criação manual de tickets na carga inicial
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const setting = await getSetting({ column: "manualTicketCreation" });
                if (mounted) setManualTicketEnabled(setting?.manualTicketCreation === "enabled");
            } catch (e) {
                if (mounted) setManualTicketEnabled(false);
            }
        })();
        return () => { mounted = false; };
    }, [getSetting]);

    useEffect(() => {
        dispatch({ type: "RESET" });
        setPageNumber(1);
    }, [status, searchParam, dispatch, showAll, tags, users, forceSearch, selectedQueueIds, whatsappIds, statusFilter, sortTickets, searchOnMessages]);

    const { tickets, hasMore, loading } = useTickets({
        pageNumber,
        searchParam,
        // Passa 'device' como status para backend tratar fila dispositivo
        status,
        showAll,
        searchOnMessages: searchOnMessages ? "true" : "false",
        tags: JSON.stringify(tags),
        users: JSON.stringify(users),
        queueIds: JSON.stringify(selectedQueueIds),
        whatsappIds: JSON.stringify(whatsappIds),
        statusFilter: JSON.stringify(statusFilter),
        userFilter,
        sortTickets
    });


    useEffect(() => {
        // const queueIds = queues.map((q) => q.id);
        // const filteredTickets = tickets.filter(
        //     (t) => queueIds.indexOf(t.queueId) > -1
        // );
        // const allticket = user.allTicket === 'enabled';
        // if (profile === "admin" || allTicket || allowGroup || allHistoric) {
        if (companyId) {
            dispatch({
                type: "LOAD_TICKETS",
                payload: tickets,
                status,
                sortDir: sortTickets
            });
        }
        // } else {
        //  dispatch({ type: "LOAD_TICKETS", payload: filteredTickets });
        // }

    }, [tickets]);

    useEffect(() => {
        const isDispositivoQueue = (t) => t?.queue?.name?.toLowerCase && t.queue.name.toLowerCase() === 'dispositivo';
    const isGroupTicket = (t) => !!(t?.isGroup || t?.contact?.isGroup);
    const matchesTab = (t) => {
      if (!t) return false;
      if (status === 'device') return isDispositivoQueue(t) && !isGroupTicket(t);
      if (status === 'group') return isGroupTicket(t) || t.status === 'group';
      if (isDispositivoQueue(t)) return false;
      return t.status === status;
    };

        const shouldUpdateTicket = (ticket) => {
            if (status === 'device') {
                return isDispositivoQueue(ticket) && !isGroupTicket(ticket);
            }
            // Fora de device/grupo, nunca mostrar fila Dispositivo
            if (status !== 'device' && status !== 'group' && isDispositivoQueue(ticket)) return false;

            return (
                (!ticket?.userId || ticket?.userId === user?.id || showAll) &&
                ((!ticket?.queueId && showTicketWithoutQueue) || selectedQueueIds.indexOf(ticket?.queueId) > -1)
            );
        }

        const notBelongsToUserQueues = (ticket) =>
            ticket.queueId && selectedQueueIds.indexOf(ticket.queueId) === -1;

        const onCompanyTicketTicketsList = (data) => {
            // console.log("onCompanyTicketTicketsList", data)
            if (data.action === "updateUnread") {
                dispatch({
                    type: "RESET_UNREAD",
                    payload: data.ticketId,
                    status: status,
                    sortDir: sortTickets
                });
            }
            // Em modo manual, ignorar atualizações de tickets oficiais (não pertencem à lista de sessões)
            if (manualTicketEnabled && data.action === "update" && data?.ticket?.isOfficial) {
                return;
            }
            // console.log(shouldUpdateTicket(data.ticket))
            if (data.action === "update" &&
                shouldUpdateTicket(data.ticket) && matchesTab(data.ticket)) {
                dispatch({
                    type: "UPDATE_TICKET",
                    payload: data.ticket,
                    status: status,
                    sortDir: sortTickets
                });
            }

            if (data.action === "update" && status !== 'device' && status !== 'group' && notBelongsToUserQueues(data.ticket)) {
                dispatch({
                    type: "DELETE_TICKET",
                    payload: data.ticket?.id,
                    status: status,
                    sortDir: sortTickets
                });
            }

            if (data.action === "delete") {
                dispatch({
                    type: "DELETE_TICKET", payload: data?.ticketId, status: status,
                    sortDir: sortTickets
                });

            }
        };

        const onCompanyAppMessageTicketsList = (data) => {
            // Em modo manual, ignorar criação de tickets oficiais (não pertencem à lista de sessões)
            if (manualTicketEnabled && data.action === "create" && data?.ticket?.isOfficial) {
                return;
            }
            if (data.action === "create" &&
                shouldUpdateTicket(data.ticket) && matchesTab(data.ticket)) {
                dispatch({
                    type: "UPDATE_TICKET_UNREAD_MESSAGES",
                    payload: data.ticket,
                    status: status,
                    sortDir: sortTickets
                });
            }
            // else if (data.action === "create" && shouldUpdateTicketUser(data.ticket) && data.ticket.status === status) {
            //     dispatch({
            //         type: "UPDATE_TICKET_UNREAD_MESSAGES",
            //         payload: data.ticket,
            //     });
            // }
        };

        const onCompanyContactTicketsList = (data) => {
            if (data.action === "update" && data.contact) {
                dispatch({
                    type: "UPDATE_TICKET_CONTACT",
                    payload: data.contact,
                    status: status,
                    sortDir: sortTickets
                });
            }
        };

        const onConnectTicketsList = () => {
            if (status) {
                socket.emit("joinTickets", status);
            } else {
                socket.emit("joinNotification");
            }
        }

    socket.on("connect", onConnectTicketsList)
        socket.on(`company-${companyId}-ticket`, onCompanyTicketTicketsList);
        socket.on(`company-${companyId}-appMessage`, onCompanyAppMessageTicketsList);
        socket.on(`company-${companyId}-contact`, onCompanyContactTicketsList);

        return () => {
            if (status) {
                socket.emit("leaveTickets", status);
            } else {
                socket.emit("leaveNotification");
            }
            socket.off("connect", onConnectTicketsList);
            socket.off(`company-${companyId}-ticket`, onCompanyTicketTicketsList);
            socket.off(`company-${companyId}-appMessage`, onCompanyAppMessageTicketsList);
            socket.off(`company-${companyId}-contact`, onCompanyContactTicketsList);
        };

    }, [status, showAll, user, selectedQueueIds, tags, users, profile, queues, sortTickets, showTicketWithoutQueue, manualTicketEnabled, companyId, socket]);

    useEffect(() => {
        if (typeof updateCount === "function") {
            updateCount(ticketsList.length);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticketsList]);

    const loadMore = () => {
        setPageNumber((prevState) => prevState + 1);
    };

    const handleScroll = (e) => {
        if (!hasMore || loading) return;

        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

        if (scrollHeight - (scrollTop + 100) < clientHeight) {
            loadMore();
        }
    };

    if (status && status !== "search") {
        const isGroupTicket = (t) => !!(t?.isGroup || t?.contact?.isGroup);
        if (status === "device") {
            ticketsList = ticketsList.filter(ticket => {
                const queueName = ticket?.queue?.name;
                const isDeviceQueue = typeof queueName === 'string' && queueName.trim().toLowerCase() === 'dispositivo';
                return isDeviceQueue && !isGroupTicket(ticket) && ['open', 'pending'].includes(ticket?.status);
            });
        } else if (status === "group") {
            ticketsList = ticketsList.filter(ticket => (isGroupTicket(ticket) || ticket.status === 'group'));
        } else {
            ticketsList = ticketsList.filter(ticket => {
                const queueName = ticket?.queue?.name;
                const isDeviceQueue = typeof queueName === 'string' && queueName.trim().toLowerCase() === 'dispositivo';
                return ticket.status === status && !isDeviceQueue;
            });
        }
    }

    // Filtro defensivo: em modo manual, a lista "Atendendo" (sessões) não deve mostrar tickets oficiais
    // Opcionalmente restringir à aba 'open'
    // if (manualTicketEnabled && status === 'open') {
    //   ticketsList = ticketsList.filter((t) => !t?.isOfficial);
    // }

    return (
        <Paper className={classes.ticketsListWrapper} style={style}>
            <Paper
                square
                name="closed"
                elevation={0}
                className={classes.ticketsList}
                onScroll={handleScroll}
            >
                <List style={{ paddingTop: 0 }} >
                    {ticketsList.length === 0 && !loading ? (
                        <div className={classes.noTicketsDiv}>
                            <span className={classes.noTicketsTitle}>
                                {i18n.t("ticketsList.noTicketsTitle")}
                            </span>
                            <p className={classes.noTicketsText}>
                                {i18n.t("ticketsList.noTicketsMessage")}
                            </p>
                        </div>
                    ) : (
                        <>
                            {ticketsList.map((ticket) => (
                                // <List key={ticket.id}>
                                //     {console.log(ticket)}
                                <TicketListItem
                                    ticket={ticket}
                                    key={ticket.id}
                                    setTabOpen={setTabOpen}
                                />
                                // </List>
                            ))}
                        </>
                    )}
                    {loading && <TicketsListSkeleton />}
                </List>
            </Paper>
        </Paper>
    );
};

export default TicketsListCustom;
